import type { BoxSummary, CopyRun, OpenRecord, VolatilityWords } from "@loot-os/api-contract";
import type { ReactElement, ReactNode } from "react";

import type { BoxCardTopCard } from "../../domain/BoxCard.js";
import type { PackTearProps } from "../../moments/PackTear.js";
import type { FooterProps } from "../../patterns/Footer.js";
import type { MobileTab } from "../../patterns/MobileBars.js";
import type { NavLinkProps } from "../../patterns/NavLink.js";

/**
 * What templates are given, shared by every page: the app resolves it (the storefront's content
 * hooks), a template only lays it out. Records arrive as the API returns them (`BoxSummary`,
 * `OpenRecord`); every word arrives already in the store's terminology.
 */

/** Somewhere a template links to, in the app's own URL space. */
export interface TemplateLink {
  href: string;
}

/** A labeled link a template renders as a button. */
export interface TemplateAction extends TemplateLink {
  label: string;
  emphasis: "primary" | "secondary";
}

/** Prose with inline links: a template renders each link in its own style. */
export type RichText = ReadonlyArray<string | { text: string; link: TemplateLink }>;

/**
 * Copy's runs of prose (`CopyRun`) as rich text, each link to its page, or to `everyLinkTo` where
 * given (the theme preview's `#`).
 */
export function toRichText(runs: readonly CopyRun[], everyLinkTo?: string): RichText {
  return runs.map((run) =>
    typeof run === "string" ? run : { text: run.text, link: { href: everyLinkTo ?? run.href } },
  );
}

/** A link or action a template renders as a button or link (chrome, box page, optional sections). */
export interface ActionContent {
  label: string;
  href?: string;
  render?: ReactElement;
  onClick?: () => void;
}

/** The heart on a box card: whether the shopper saved the box, and the press that flips it. */
export interface FavoriteToggle {
  /**
   * Undefined while the shopper's favorites load: the heart shows unsaved, and their arrival is not
   * taken for a save (which would play it).
   */
  saved: boolean | undefined;
  /** The heart's accessible name; `saved` is its pressed state. */
  label: string;
  onToggle: () => void;
}

/** A box as a card that links to its page, with the shopper's favorite toggle on it. */
export interface BoxCardContent {
  box: BoxSummary;
  /** The box's page. */
  href: string;
  favorite: FavoriteToggle;
  /** The most valuable item in it, in cents, when the app knows it. */
  topPull?: number;
  /** The most valuable item's picture, which peeks out behind the art (`BoxCard`'s `topCard`). */
  topCard?: BoxCardTopCard;
  /** A short tag in the art's corner: a game, a theme, NEW. */
  tag?: string;
  /** How swingy the box is (`BoxCard`'s `volatility`); left out where the store doesn't show it. */
  volatility?: number | null;
  /** The volatility meter's words, from the store's copy (`VOLATILITY_WORDS` by default). */
  volatilityWords?: VolatilityWords;
}

/** One open in a best-opens row. */
export interface RankedOpen {
  open: OpenRecord;
  /** The item's rarity tier, named as the odds table and the opener's rails name it. */
  tierLabel: string;
  /** That tier's color as `#rrggbb`, with the tenant's recoloring; the design system reads its token. */
  tierColor: string;
  /** The accessible name of the card that opens the pull's details. */
  detailsLabel: string;
}

/** The store's chrome: identity, navigation, money and footer. */
export interface StoreChrome {
  name: string;
  logoSrc?: string;
  links: readonly (NavLinkProps & { id: string })[];
  mobileTabs: readonly MobileTab[];
  searchLabel: string;
  /** Runs a search from the nav's field (the app takes the shopper to its results). */
  onSearch?: (query: string) => void;
  /** The spendable balance in cents; leave out when signed out. */
  balance?: number;
  onBalance?: () => void;
  deposit?: ActionContent;
  /** Shown when signed out, in place of balance and deposit. */
  signIn?: ActionContent;
  /** The app's own controls after the money, on both bars: notifications, an account menu. */
  actions?: ReactNode;
  footer: Omit<FooterProps, "logo" | "lead">;
  /**
   * The pack tear across the top of the footer: a sealed strip the shopper rips to reveal a card.
   * A sample, never an open. Drawn where the theme asks for it (`layout.footer: "tear"`).
   */
  footerTear?: FooterTearContent;
}

/** The footer's pack tear: the cards it can reveal, its words and the way on. */
export interface FooterTearContent {
  pulls: PackTearProps["pulls"];
  words: PackTearProps["words"];
  /** Beside Reset once a card is out: the store's packs. */
  action: ActionContent;
}
